/// <reference types="vite/client" />
import {
  brandConfig,
  BrandMessageIds,
  buildBrandOgImageUrl,
} from "@nitro-ape/shared/lib/brand-config";
import { getEnv } from "@/lib/env";
import { ReactScan } from "@/components/ReactScan";
import { ConfirmationDialogProvider } from "@/providers/confirmation-dialog-provider";
import { HeroUIProvider } from "@heroui/system";
import { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster } from "@/components/common/Toaster";
import appCss from "./globals.css?url";
import { TooltipProvider } from "@/components/common/Tooltip";
import { I18nProvider } from "@lingui/react";
import type { I18n } from "@lingui/core";
import { PrivyProvider } from "@/providers/privy-provider";

import {
  defaultLocale,
  i18n as i18nGlobal,
  supportedLocales,
  type SupportedLocale,
} from "@/lib/i18n";
import { isDevelopment } from "@/lib/utils";

export interface AppRouterContext {
  queryClient: QueryClient;
  locale?: SupportedLocale;
  i18n?: I18n;
}

const ogLocaleByLocale: Record<SupportedLocale, string> = {
  en: "en_US",
  lt: "lt_LT",
  ru: "ru_RU",
};

export const Route = createRootRouteWithContext<AppRouterContext>()({
  head: ({ matches }) => {
    const rootCtx = matches[0]?.context as AppRouterContext | undefined;

    const locale = rootCtx?.locale ?? defaultLocale;
    const ogLocale = ogLocaleByLocale[locale];

    const siteUrl = brandConfig(getEnv).siteUrl;
    const ogImageUrl = buildBrandOgImageUrl(getEnv);
    const activeI18n = rootCtx?.i18n ?? i18nGlobal;
    const t = (id: string) => activeI18n._(id);
    const title = t(BrandMessageIds.siteName);
    const description = t(BrandMessageIds.metaDescription);
    const keywords = t(BrandMessageIds.metaKeywords);

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title },
        { name: "description", content: description },
        { name: "keywords", content: keywords },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: title },
        { property: "og:url", content: siteUrl },
        { property: "og:image", content: ogImageUrl },
        { property: "og:locale", content: ogLocale },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImageUrl },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      ],
    };
  },
  scripts: () => [
    ...((!isDevelopment && [
      {
        src: "https://cloud.umami.is/script.js",
        "data-website-id": getEnv.VITE_UMAMI_TRACKING_ID,
        async: true,
        strategy: "afterInteractive",
      },
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-5QKXMCF7CB",
        async: true,
        strategy: "afterInteractive",
      },
      {
        children: `window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', 'G-5QKXMCF7CB');`,
      },
    ]) ||
      []),
  ],

  component: RootLayout,
});

function RootLayout() {
  const { locale: ctxLocale, i18n: ctxI18n } = Route.useRouteContext();
  const locale = (
    supportedLocales.includes(ctxLocale as SupportedLocale)
      ? (ctxLocale as SupportedLocale)
      : defaultLocale
  ) as SupportedLocale;

  const i18nForThisEnv =
    (typeof window === "undefined" ? ctxI18n : i18nGlobal) ?? i18nGlobal;

  return (
    <html lang={locale} className="fancy-scroll2 bg-background">
      <head>
        <HeadContent />
      </head>
      <body className="custom">
        {isDevelopment && <ReactScan />}
        <PrivyProvider>
          <I18nProvider i18n={i18nForThisEnv}>
            <HeroUIProvider>
              <TooltipProvider>
                <ConfirmationDialogProvider>
                  <Outlet />
                  {!isDevelopment && <SpeedInsights />}
                  <Toaster theme="dark" />
                </ConfirmationDialogProvider>
              </TooltipProvider>
            </HeroUIProvider>
          </I18nProvider>
        </PrivyProvider>
        <Scripts />
      </body>
    </html>
  );
}
